import { X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, Select } from '../../../components/ui/FormField.js';
import type { CreatableBlockKind, ProposedBlock } from '../types.js';
import { mytDateOf } from './dropLogic.js';
import {
  type HalfHourMinute,
  type Meridiem,
  clockToParts,
  instantAtDateClock,
  partsToClock,
  roundClockToHalfHour,
  windowClock,
} from './geometry.js';
import {
  onVisibleAreaChange,
  placeInView,
  touchForm,
  typingInside,
} from './keepOpenWhileTyping.js';

/**
 * 096 — the step between drawing a block and writing one.
 *
 * 🔴 **Portalled to document level on purpose.** The obvious implementation — an absolutely
 * positioned child of the lane — does not work here. The grid lives inside
 * `overflow-auto … max-h-[70vh]` (`Timeline.tsx`), so a popover on a lane near an edge would be
 * **clipped**, and the sticky header at `z-20` (with a `z-30` corner) would draw **over** it. Both
 * were read from the markup, not guessed (`research.md` D1).
 *
 * 🔴 **Not a modal.** No focus trap and no backdrop: the operator chose a popover precisely so the
 * schedule stays readable behind it. What is borrowed from `Modal.tsx` is only the part that is
 * about keyboards rather than about being modal — Escape to close, an accessible name, and a
 * labelled close control.
 *
 * It **closes on scroll or resize** rather than chasing its anchor. The popover exists to confirm
 * one drag; re-anchoring it continuously would need a positioning engine, and dismissing costs the
 * operator nothing because the drag can simply be repeated.
 */

const KIND_LABEL: Record<CreatableBlockKind, string> = {
  manual_block: 'Blocked',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  other: 'Other',
};

/** The four the endpoint accepts. `booking` and `extension` are never creatable — a hold belongs to its booking. */
const KINDS = Object.keys(KIND_LABEL) as CreatableBlockKind[];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * 096, superseded 2026-09-21: three plain selects instead of a native `<input type="time">`,
 * so the minute choice is deterministically `{00, 30}` in every browser — a native time
 * input's own `step` attribute does not reliably restrict what its picker UI itself offers.
 * Module-level (not defined inside `CreateBlockPopover`) so React does not treat it as a new
 * component type — and remount its children — on every parent render.
 */
function TimeOfDaySelect({
  label,
  date,
  clock,
  onDateChange,
  onChange,
}: {
  label: string;
  /** 382 (#1273): the MYT calendar date, `YYYY-MM-DD`, so a block can end on a later day. */
  date: string;
  clock: string;
  onDateChange: (date: string) => void;
  onChange: (clock: string) => void;
}) {
  const { hour12, minute, meridiem } = clockToParts(clock);
  const testIdBase = `create-block-${label.toLowerCase()}`;

  return (
    <div>
      <span className="text-xs text-steel">{label}</span>
      <Input
        type="date"
        aria-label={`${label} date`}
        data-testid={`${testIdBase}-date`}
        value={date}
        required
        className="mt-1 w-full"
        onChange={(e) => onDateChange((e.target as HTMLInputElement).value)}
      />
      <div className="mt-1 flex gap-1.5">
        <Select
          aria-label={`${label} hour`}
          data-testid={`${testIdBase}-hour`}
          value={hour12}
          className="min-w-0 flex-1"
          onChange={(e) =>
            onChange(partsToClock(Number((e.target as HTMLSelectElement).value), minute, meridiem))
          }
        >
          {HOURS_12.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Select>
        <Select
          aria-label={`${label} minute`}
          data-testid={`${testIdBase}-minute`}
          value={minute}
          className="min-w-0 flex-1"
          onChange={(e) =>
            onChange(
              partsToClock(
                hour12,
                Number((e.target as HTMLSelectElement).value) as HalfHourMinute,
                meridiem,
              ),
            )
          }
        >
          <option value={0}>00</option>
          <option value={30}>30</option>
        </Select>
        <Select
          aria-label={`${label} AM or PM`}
          data-testid={`${testIdBase}-meridiem`}
          value={meridiem}
          className="min-w-0 flex-1"
          onChange={(e) =>
            onChange(
              partsToClock(hour12, minute, (e.target as HTMLSelectElement).value as Meridiem),
            )
          }
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </Select>
      </div>
    </div>
  );
}

export interface CreateBlockPopoverProps {
  proposal: ProposedBlock;
  carLabel: string;
  /**
   * The window start the clock is measured from — REQUIRED.
   *
   * 🔴 An earlier draft defaulted this by flooring the proposal to a UTC day boundary. That reads
   * 01:00Z as "01:00" when the axis says "09:00" MYT — the same class of defect as 094's schedule
   * opening on yesterday. The caller knows the window; the popover must not invent one.
   */
  windowFrom: Date;
  onConfirm: (block: ProposedBlock) => void;
  onDismiss: () => void;
  /** A refusal from the platform, rendered in place so the operator can correct the range. */
  error?: string | null;
  pending?: boolean;
}

export function CreateBlockPopover({
  proposal,
  carLabel,
  windowFrom: from,
  onConfirm,
  onDismiss,
  error = null,
  pending = false,
}: CreateBlockPopoverProps) {
  // 096, superseded 2026-09-21: rounded IMMEDIATELY on init, not just at display time — a drag
  // landing on an unaligned instant (e.g. 09:47) must never leave the underlying value out of
  // step with what the three selects show, or confirming without touching them would submit
  // the unrounded time while the screen displayed the rounded one.
  const [startClock, setStartClock] = useState(() =>
    roundClockToHalfHour(
      windowClock(proposal.startAt, from, new Date(from.getTime() + 86_400_000)),
    ),
  );
  const [endClock, setEndClock] = useState(() =>
    roundClockToHalfHour(windowClock(proposal.endAt, from, new Date(from.getTime() + 86_400_000))),
  );
  // 382 (#1273): the dates default to the drawn day, so a same-day block costs no extra tap.
  const [startDate, setStartDate] = useState(() => mytDateOf(proposal.startAt.getTime()));
  const [endDate, setEndDate] = useState(() => mytDateOf(proposal.endAt.getTime()));
  const [kind, setKind] = useState<CreatableBlockKind>(proposal.type);
  const [note, setNote] = useState(proposal.reason);
  const [localError, setLocalError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  /**
   * 🔴 Measured after mount, not guessed.
   *
   * The first implementation always placed the popover at `anchor.bottom + 8`. Dragging the
   * bottom-most lane of a scrolled grid then put it at top=810 with a height of 263 in a 900px
   * viewport — 173px below the fold, taking the Create button with it. Found in a browser
   * (quickstart L11), because jsdom has no layout and could never have shown it.
   *
   * Starts unplaced and invisible for one frame so the flip is never seen as a jump.
   */
  const [placement, setPlacement] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    const place = () => {
      const { width, height } = el.getBoundingClientRect();
      setPlacement(placeInView(proposal.anchorRect, { width, height }));
    };
    place();

    // Observing the box rather than listing the things that might change its height. An error
    // message appearing makes it taller, but so would anything else added later — and a
    // dependency list is one forgotten entry away from a popover that no longer fits.
    const ro = new ResizeObserver(place);
    ro.observe(el);
    // 404 (#1329): the keyboard opening or closing moves the visible area; follow it.
    const stop = onVisibleAreaChange(place);
    return () => {
      ro.disconnect();
      stop();
    };
  }, [proposal.anchorRect]);

  // Escape closes — same shape as Modal.tsx's handler, without the rest of Modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  // A click anywhere else dismisses. Pointerdown rather than click, so a drag that begins outside
  // cannot leave the popover open behind it.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      // 404 (#1329): the first tap outside while typing on a touch screen only closes the keyboard.
      if (typingInside(boxRef.current)) return;
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onDismiss();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [onDismiss]);

  // Contract C12: close rather than float away from the bar it points at.
  useEffect(() => {
    // 404 (#1329): never for a pop-up with a field on a touch screen — the phone keyboard resizes and scrolls the
    // window as it opens and closes; the pop-up is re-placed above it instead (placement effect).
    const close = () => {
      if (!touchForm(boxRef.current)) onDismiss();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onDismiss]);

  const submit = () => {
    const startAt = instantAtDateClock(startDate, startClock);
    const endAt = instantAtDateClock(endDate, endClock);
    if (!startAt || !endAt) {
      setLocalError('Enter both a start and an end date and time.');
      return;
    }
    // FR-005: refused here, before anything is sent. Equal is invalid, not just reversed.
    if (endAt.getTime() <= startAt.getTime()) {
      setLocalError('The end must be after the start.');
      return;
    }
    setLocalError(null);
    onConfirm({
      ...proposal,
      startAt,
      endAt,
      type: kind,
      // Empty means send no reason at all, never an empty string.
      reason: note.trim(),
    });
  };

  const shown = localError ?? error;

  return createPortal(
    <div
      ref={boxRef}
      data-testid="create-block-popover"
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={`New block on ${carLabel}`}
      // Fixed, from a viewport rectangle — see the portal note above.
      style={{
        position: 'fixed',
        left: placement?.left ?? proposal.anchorRect.left,
        top: placement?.top ?? proposal.anchorRect.bottom + 8,
        // Invisible for the single frame before it has been measured, so the flip is never
        // rendered as a jump.
        visibility: placement ? 'visible' : 'hidden',
        zIndex: 50,
      }}
      className="w-72 rounded-xl border border-hairline bg-canvas p-3 shadow-xl motion-safe:transition-opacity"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink">New block · {carLabel}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="text-steel hover:text-ink motion-safe:transition-colors"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {/* 096, superseded 2026-09-21 (operator feedback on the 3-select redesign): stacked, not
          side-by-side — six selects sharing a 288px-wide popover's two halves left each one too
          narrow to read (operator screenshot). Each group now gets the full popover width. */}
      <div className="flex flex-col gap-2">
        <TimeOfDaySelect
          label="Start"
          date={startDate}
          clock={startClock}
          onDateChange={setStartDate}
          onChange={setStartClock}
        />
        <TimeOfDaySelect
          label="End"
          date={endDate}
          clock={endClock}
          onDateChange={setEndDate}
          onChange={setEndClock}
        />
      </div>

      <label className="mt-2 block text-xs text-steel" htmlFor="create-block-kind">
        Kind
        <Select
          id="create-block-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as CreatableBlockKind)}
          className="mt-1 w-full"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </Select>
      </label>

      <label className="mt-2 block text-xs text-steel" htmlFor="create-block-note">
        Note <span className="text-steel">(optional)</span>
        <Input
          id="create-block-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why is this car unavailable?"
          className="mt-1 w-full"
        />
      </label>

      {shown && (
        <p role="alert" className="mt-2 text-xs text-semantic-danger-text">
          {shown}
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1 text-sm text-slate hover:text-ink motion-safe:transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={pending}
          className="rounded bg-brand-blue px-3 py-1 text-sm text-on-primary disabled:opacity-50 motion-safe:transition-colors"
        >
          {pending ? 'Creating…' : 'Create block'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
